import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileWarning, RefreshCw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button, Card, Input } from "@/components/ui";
import { api, ApiError, type AdminTenant } from "@/lib/api";
import { effectiveOverdueSettings, OVERDUE_AUDIENCE_LABEL, OVERDUE_AUDIENCES, overdueBlockState, overdueInvoices, type OverdueAudience, type OverdueOverride, type OverdueWarningSettings } from "@logiful/domain";

const euro = (v: number) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(v);
const errText = (e: unknown) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Nicht gespeichert") : "Nicht gespeichert");

function SettingsForm({ value, onChange, disabled }: { value: OverdueWarningSettings; onChange: (v: OverdueWarningSettings) => void; disabled?: boolean }) {
  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-[13.5px]">
        <input type="checkbox" disabled={disabled} checked={value.enabled} onChange={(e) => onChange({ ...value, enabled: e.target.checked })} />
        <span className="font-medium">Warnung anzeigen</span>
      </label>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-[12.5px] font-medium text-fg-muted">Ab Tagen nach Rechnungsdatum</span>
          <Input className="mt-1" type="number" min={1} max={365} disabled={disabled} value={value.afterDays} onChange={(e) => onChange({ ...value, afterDays: Math.max(1, Math.min(365, Number(e.target.value) || 1)) })} />
        </label>
        <label className="block">
          <span className="text-[12.5px] font-medium text-fg-muted">Wer sieht das Banner?</span>
          <select disabled={disabled} value={value.audience} onChange={(e) => onChange({ ...value, audience: e.target.value as OverdueAudience })} className="mt-1 h-9 w-full rounded-md border border-border bg-surface px-2 text-[13.5px]">
            {OVERDUE_AUDIENCES.map((a) => <option key={a} value={a}>{OVERDUE_AUDIENCE_LABEL[a]}</option>)}
          </select>
        </label>
      </div>
      {(["de", "en"] as const).map((l) => (
        <label key={l} className="block">
          <span className="text-[12.5px] font-medium text-fg-muted">Text {l === "de" ? "Deutsch" : "Englisch"}</span>
          <textarea disabled={disabled} maxLength={600} value={value.text[l]} onChange={(e) => onChange({ ...value, text: { ...value.text, [l]: e.target.value } })} className="mt-1 w-full rounded-md border border-border bg-surface px-2.5 py-2 text-[13.5px] min-h-[64px]" />
        </label>
      ))}
      <div className="rounded-lg border border-border p-3 space-y-3">
        <label className="flex items-center gap-2 text-[13.5px]">
          <input type="checkbox" disabled={disabled} checked={value.block.enabled} onChange={(e) => onChange({ ...value, block: { ...value.block, enabled: e.target.checked, afterDays: Math.max(value.block.afterDays, value.afterDays + 1) } })} />
          <span className="font-medium">Aufträge pausieren</span>
        </label>
        <p className="text-[12px] text-fg-muted">Neue Aufträge gehen nicht mehr ans Lager, bis bezahlt ist (bereits übertragene laufen weiter). Nach dem Zahlungseingang geben der stündliche Abgleich bzw. „Jetzt abgleichen“ sie automatisch frei. „Übertragung erzwingen“ durch den Kunden umgeht die Pause nicht.</p>
        {value.block.enabled && (
          <>
            <label className="block">
              <span className="text-[12.5px] font-medium text-fg-muted">Ab Tagen nach Rechnungsdatum (Vorwarnzeit: {Math.max(0, value.block.afterDays - value.afterDays)} Tage nach Beginn der Warnung)</span>
              <Input className="mt-1" type="number" min={value.afterDays + 1} max={365} disabled={disabled} value={value.block.afterDays} onChange={(e) => onChange({ ...value, block: { ...value.block, afterDays: Math.max(1, Math.min(365, Number(e.target.value) || 1)) } })} />
              {value.block.afterDays <= value.afterDays && <span className="mt-1 block text-[12px] text-danger">Muss später als die Warnung liegen.</span>}
            </label>
            {(["de", "en"] as const).map((l) => (
              <label key={l} className="block">
                <span className="text-[12.5px] font-medium text-fg-muted">Text während der Pause – {l === "de" ? "Deutsch" : "Englisch"}</span>
                <textarea disabled={disabled} maxLength={600} value={value.blockedText[l]} onChange={(e) => onChange({ ...value, blockedText: { ...value.blockedText, [l]: e.target.value } })} className="mt-1 w-full rounded-md border border-border bg-surface px-2.5 py-2 text-[13.5px] min-h-[64px]" />
              </label>
            ))}
          </>
        )}
      </div>
      <p className="text-[12px] text-fg-subtle">Platzhalter: <code>{"{anzahl}"}</code> überfällige Rechnungen, <code>{"{betrag}"}</code> offener Betrag, <code>{"{tage}"}</code> Tage seit der ältesten Rechnung, <code>{"{pausedatum}"}</code> Beginn der Pause. Mit „Aufträge pausieren“ zeigt das Banner zusätzlich „Aufträge werden ab dem … pausiert“. Lastschriften, die noch laufen, zählen nicht als überfällig.</p>
    </div>
  );
}

/** Logiful intern: Warnung bei überfälligen Rechnungen – Standard (Admins) und Ausnahmen je Kunde (Team) */
export function OverdueWarningCard({ admin, tenants }: { admin: boolean; tenants: AdminTenant[] }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["overdue-warning"], queryFn: api.admin.overdueWarning });
  const [draft, setDraft] = useState<OverdueWarningSettings | null>(null);
  const [editing, setEditing] = useState<AdminTenant | null>(null);
  const [pick, setPick] = useState("");
  const save = useMutation({
    mutationFn: (v: OverdueWarningSettings) => api.admin.saveOverdueWarning(v),
    onSuccess: () => {
      toast.success("Gespeichert", { description: "Gilt für alle Kunden ohne eigene Einstellung." });
      setDraft(null);
      void qc.invalidateQueries({ queryKey: ["overdue-warning"] });
      void qc.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (e) => toast.error(errText(e)),
  });
  const refresh = useMutation({
    mutationFn: api.admin.refreshOpenInvoices,
    onSuccess: () => {
      toast.success("Abgleich gestartet", { description: "Neuer Stand in etwa einer Minute." });
      setTimeout(() => void qc.invalidateQueries({ queryKey: ["admin-tenants"] }), 60_000);
    },
  });
  const defaults = q.data?.defaults;
  if (!defaults) return null;
  const value = draft ?? defaults;
  const rows = tenants
    .map((t) => {
      const s = effectiveOverdueSettings(defaults, t.overdueWarning);
      return { t, s, o: overdueInvoices(t.openInvoices ?? null, s.afterDays), open: (t.openInvoices?.invoices ?? []).reduce((x, i) => x + i.open, 0) };
    })
    .filter((r) => r.open > 0 || r.t.overdueWarning)
    .sort((a, b) => b.o.amount - a.o.amount || b.open - a.open);

  return (
    <Card className="mt-6 p-5">
      <div className="flex items-start gap-3">
        <FileWarning className="size-5 text-fg-muted mt-0.5" />
        <div className="min-w-0 flex-1">
          <h2 className="text-[15px] font-semibold">Überfällige Rechnungen</h2>
          <p className="text-[12.5px] text-fg-muted mt-0.5">Banner ganz oben im Portal des Kunden, wenn Rechnungen aus der Abrechnung überfällig sind. Abgleich stündlich.</p>
        </div>
        <Button size="sm" variant="secondary" icon={<RefreshCw className="size-3.5" />} loading={refresh.isPending} onClick={() => refresh.mutate()}>Jetzt abgleichen</Button>
      </div>
      <div className="mt-4">
        <SettingsForm value={value} onChange={setDraft} disabled={!admin} />
      </div>
      {admin && draft && (
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setDraft(null)}>Verwerfen</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(draft)}>Standard speichern</Button>
        </div>
      )}
      <div className="mt-5">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-[13px] font-semibold flex-1">Kunden mit offenen Rechnungen oder eigener Einstellung</h3>
          <select value={pick} onChange={(e) => { const t = tenants.find((x) => x.id === e.target.value); if (t) setEditing(t); setPick(""); }} className="h-8 rounded-lg border border-border bg-surface px-2 text-[12.5px]" aria-label="Kunde einstellen">
            <option value="">Kunde einstellen …</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        {rows.length ? (
          <ul className="mt-2 divide-y divide-border rounded-xl border border-border">
            {rows.map(({ t, s, o, open }) => (
              <li key={t.id} className="flex items-center gap-3 px-3 py-2 text-[13px]">
                <span className="min-w-0 flex-1 truncate font-medium">{t.name}</span>
                <span className="text-fg-muted tabular-nums">{euro(open)} offen</span>
                <span className={o.count && s.enabled ? "text-danger font-medium" : "text-fg-muted"}>{o.count ? `${o.count} überfällig${s.enabled ? " · Banner" : " · Warnung aus"}` : "nicht überfällig"}{(() => { const b = overdueBlockState(t.openInvoices ?? null, s); return b ? (b.blocked ? " · Aufträge pausiert" : ` · Pause ab ${new Date(`${b.pauseDate}T12:00:00`).toLocaleDateString("de-DE")}`) : ""; })()}</span>
                {t.overdueWarning && <span className="rounded-full bg-coral/10 px-1.5 py-px text-[11px] text-coral">eigene Einstellung</span>}
                <Button size="sm" variant="secondary" onClick={() => setEditing(t)}>Anpassen</Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-1 text-[12.5px] text-fg-muted">Keine offenen Rechnungen im letzten Abgleich.</p>
        )}
      </div>
      {editing && <OverrideDialog tenant={editing} defaults={defaults} onClose={() => setEditing(null)} />}
    </Card>
  );
}

function OverrideDialog({ tenant, defaults, onClose }: { tenant: AdminTenant; defaults: OverdueWarningSettings; onClose: () => void }) {
  const qc = useQueryClient();
  const [v, setV] = useState<OverdueWarningSettings>(() => effectiveOverdueSettings(defaults, tenant.overdueWarning));
  const save = useMutation({
    mutationFn: (override: OverdueOverride | null) => api.admin.saveTenantOverdueWarning(tenant.id, override),
    onSuccess: () => {
      toast.success(`${tenant.name}: gespeichert`);
      void qc.invalidateQueries({ queryKey: ["admin-tenants"] });
      void qc.invalidateQueries({ queryKey: ["me"] });
      onClose();
    },
    onError: (e) => toast.error(errText(e)),
  });
  // nur Abweichungen vom Standard speichern – Änderungen am Standard wirken sonst weiter
  const diff = (): OverdueOverride => {
    const o: OverdueOverride = {};
    if (v.enabled !== defaults.enabled) o.enabled = v.enabled;
    if (v.afterDays !== defaults.afterDays) o.afterDays = v.afterDays;
    if (v.audience !== defaults.audience) o.audience = v.audience;
    if (v.text.de !== defaults.text.de || v.text.en !== defaults.text.en) o.text = v.text;
    if (v.block.enabled !== defaults.block.enabled || v.block.afterDays !== defaults.block.afterDays) o.block = v.block;
    if (v.blockedText.de !== defaults.blockedText.de || v.blockedText.en !== defaults.blockedText.en) o.blockedText = v.blockedText;
    return o;
  };
  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(600px,calc(100vw-32px))] max-h-[85vh] overflow-y-auto -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-surface p-5 shadow-xl">
          <Dialog.Title className="text-[16px] font-semibold">Überfällige Rechnungen – {tenant.name}</Dialog.Title>
          <Dialog.Description className="mt-1 text-[12.5px] text-fg-muted">Abweichungen vom Standard gelten nur für diesen Kunden.</Dialog.Description>
          <div className="mt-4">
            <SettingsForm value={v} onChange={setV} />
          </div>
          <div className="mt-4 flex flex-wrap justify-between gap-2">
            <Button variant="ghost" disabled={save.isPending} onClick={() => save.mutate(null)}>Auf Standard zurücksetzen</Button>
            <span className="flex gap-2">
              <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
              <Button variant="primary" loading={save.isPending} onClick={() => { const d = diff(); save.mutate(Object.keys(d).length ? d : null); }}>Speichern</Button>
            </span>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
